import { DxFilter, getReportFilterIdentifier } from '@pendo/middleware';
import { RouterQuery } from './router-query-filters';
import { getOperatorNameByLabel, isFilterValid } from '@/common/event-property-filter/utils';

export const DASHBOARD_METADATA_FILTER_ID = 'dashboard-metadata-filter';

export interface DashboardMetadataFilter {
    id?: string;
    field?: string;
    operator?: string;
    kind?: string;
    filterType?: string;
    group?: string;
    name?: string;
    comparisonValues?: (string | number | boolean)[];
    valid?: boolean;
}

// A dashboard metadata filter is always a single filter (AC: pick one field).
// It's stored as an array to keep the shape compatible with event-property-filter-section.
export type DashboardMetadataFilters = DashboardMetadataFilter[];

// UTF-8-safe base64 helpers. btoa/atob operate on Latin-1 code units, so we
// funnel non-ASCII characters (e.g. Japanese metadata values) through
// encodeURIComponent first. In Node (jest) we fall back to Buffer.
const encode = (value: string): string => {
    if (typeof btoa === 'function') {
        return btoa(unescape(encodeURIComponent(value)));
    }

    return Buffer.from(value, 'utf-8').toString('base64');
};

const decode = (value: string): string => {
    if (typeof atob === 'function') {
        return decodeURIComponent(escape(atob(value)));
    }

    return Buffer.from(value, 'base64').toString('utf-8');
};

// Operators such as "is true" and "is empty" are valid with no comparison
// values, so defer to the shared rules instead of always requiring them.
const isValid = (filter: DashboardMetadataFilter): boolean => isFilterValid({
    field: filter.field,
    operator: filter.operator,
    comparisonValues: filter.comparisonValues,
});

export const formatMetadataFilterForQuery = (metadataFilter: DashboardMetadataFilters) => {
    const [filter] = metadataFilter || [];
    if (!filter || !isValid(filter)) {
        return { metadataFilter: undefined };
    }

    try {
        return { metadataFilter: encode(JSON.stringify(filter)) };
    } catch {
        return { metadataFilter: undefined };
    }
};

export const convertQueryToMetadataFilter = ({ metadataFilter }: RouterQuery) => {
    if (typeof metadataFilter !== 'string' || !metadataFilter) return {};

    try {
        const parsed = JSON.parse(decode(metadataFilter));
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        // The query string is user-editable, so type-check the payload before
        // handing it to the shared validity rules.
        if (typeof parsed.field !== 'string' || !parsed.field) return {};
        if (parsed.comparisonValues != null && !Array.isArray(parsed.comparisonValues)) return {};
        if (!isValid(parsed)) return {};

        return {
            metadataFilter: [
                {
                    ...parsed,
                    comparisonValues: parsed.comparisonValues ?? [],
                    id: DASHBOARD_METADATA_FILTER_ID,
                    valid: true,
                } as DashboardMetadataFilter,
            ],
        };
    } catch {
        return {};
    }
};

type DxFilterFromPicker = {
    [K in keyof DxFilter]: DashboardMetadataFilter[K & keyof DashboardMetadataFilter];
};

const toDxFilter = (filter: DashboardMetadataFilter): DxFilter => {
    const dxFilter: DxFilterFromPicker = {
        id: filter.id,
        field: filter.field,
        kind: filter.kind,
        filterType: filter.filterType,
        operator: filter.operator,
        comparisonValues: filter.comparisonValues,
        group: filter.group,
        name: filter.name,
    };

    return dxFilter as DxFilter;
};

const hasResolvableIdentifier = (filter: DxFilter): boolean => {
    try {
        return !!getReportFilterIdentifier(filter);
    } catch {
        return false;
    }
};

export const toAggMetadataFilters = (metadataFilter?: DashboardMetadataFilters): DxFilter[] => {
    const selectedFilters = Array.isArray(metadataFilter) ? metadataFilter : [];

    return selectedFilters
        .filter(isValid)
        .map(toDxFilter)
        .filter(hasResolvableIdentifier);
};

export const formatMetadataFiltersLabel = (metadataFilters: DxFilter[] = []): string =>
    metadataFilters
        .map(({ name, field, operator, comparisonValues }) => {
            const condition = operator ? getOperatorNameByLabel(operator) : '';
            const values = Array.isArray(comparisonValues) ? comparisonValues.join(', ') : '';

            return [name || field, condition, values].filter(Boolean).join(' ');
        })
        .join('; ');
